import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, X } from "lucide-react";
import { useCookieConsent } from "@/context/CookieConsentContext";

const TOGGLES = [
  {
    key: "necessary",
    label: "Szükséges",
    desc: "Bejelentkezés, biztonság, süti-választás megjegyzése. Ezek nélkül az oldal nem működik megbízhatóan.",
    locked: true,
  },
  {
    key: "functional",
    label: "Funkcionális",
    desc: "Téma, kedvencek, felhasználói beállítások a böngészőben — kényelmi funkciók.",
    locked: false,
  },
  {
    key: "statistics",
    label: "Statisztika",
    desc: "Anonym látogatottság-mérés (saját szerver + PostHog), fejlesztéshez. Személyre szabott hirdetés nélkül.",
    locked: false,
  },
  {
    key: "marketing",
    label: "Marketing",
    desc: "Jelenleg nem használunk marketing sütit. A kapcsoló a jövőbeli kampányokhoz készült.",
    locked: false,
    disabled: true,
  },
];

export function CookieConsentBanner() {
  const { decided, settingsOpen, acceptAll, rejectOptional, saveCustom, openSettings, closeSettings, consent } =
    useCookieConsent();
  const [draft, setDraft] = useState({
    functional: consent.functional,
    statistics: consent.statistics,
    marketing: false,
  });

  const showBar = !decided || settingsOpen;

  if (!showBar) return null;

  const openCustomize = () => {
    setDraft({
      functional: consent.functional,
      statistics: consent.statistics,
      marketing: false,
    });
    openSettings();
  };

  return (
    <>
      {!decided && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-[2px] pointer-events-none"
          aria-hidden
        />
      )}
      <div
        data-testid="cookie-consent-banner"
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-modal="true"
        className="fixed z-[201] bottom-0 left-0 right-0 p-4 sm:p-6 pointer-events-none"
      >
        <div className="pointer-events-auto max-w-3xl mx-auto glass-strong border border-white/15 rounded-2xl shadow-2xl p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <Cookie className="w-6 h-6 text-electric shrink-0 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1 min-w-0">
              {settingsOpen ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <h2 id="cookie-consent-title" className="font-heading text-lg font-medium text-white">
                      Süti beállítások
                    </h2>
                    {decided && (
                      <button
                        type="button"
                        data-testid="cookie-settings-close"
                        onClick={closeSettings}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
                        aria-label="Bezárás"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <ul className="space-y-4 mb-5">
                    {TOGGLES.map((t) => (
                      <li key={t.key} className="rounded-xl border border-white/10 p-3 bg-white/[0.02]">
                        <label className={`flex items-start gap-3 ${t.locked || t.disabled ? "opacity-90" : "cursor-pointer"}`}>
                          <input
                            type="checkbox"
                            data-testid={`cookie-toggle-${t.key}`}
                            className="mt-1 h-4 w-4 rounded accent-electric"
                            checked={t.locked ? true : !!draft[t.key]}
                            disabled={t.locked || t.disabled}
                            onChange={(e) => {
                              if (t.locked || t.disabled) return;
                              setDraft((d) => ({ ...d, [t.key]: e.target.checked }));
                            }}
                          />
                          <span>
                            <span className="text-sm font-medium text-white block">{t.label}</span>
                            <span className="text-xs text-slate-400 leading-relaxed block mt-1">{t.desc}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="cookie-save-custom"
                      onClick={() => saveCustom(draft)}
                      className="px-4 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium"
                    >
                      Mentés
                    </button>
                    <button
                      type="button"
                      data-testid="cookie-accept-all-settings"
                      onClick={acceptAll}
                      className="px-4 py-2.5 rounded-full border border-white/15 text-sm text-slate-200 hover:bg-white/5"
                    >
                      Elfogadom az összeset
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 id="cookie-consent-title" className="font-heading text-lg font-medium text-white">
                    Sütik és adatvédelem
                  </h2>
                  <p className="text-sm text-slate-300 leading-relaxed mt-2">
                    Sütiket és helyi tárolót használunk: a szükségesek a bejelentkezéshez kellenek; a
                    funkcionálisak a témát és kedvenceket mentik; a statisztikaiak segítenek fejleszteni az
                    oldalt (PostHog, látogatottság). Részletek az{" "}
                    <Link to="/adatvedelem#sutik" className="text-electric hover:underline">
                      adatvédelmi tájékoztatóban
                    </Link>
                    .
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 mt-5">
                    <button
                      type="button"
                      data-testid="cookie-accept-all"
                      onClick={acceptAll}
                      className="px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium"
                    >
                      Elfogadom az összeset
                    </button>
                    <button
                      type="button"
                      data-testid="cookie-reject-optional"
                      onClick={rejectOptional}
                      className="px-5 py-2.5 rounded-full border border-white/20 text-sm text-slate-200 hover:bg-white/5"
                    >
                      Csak szükséges
                    </button>
                    <button
                      type="button"
                      data-testid="cookie-customize"
                      onClick={openCustomize}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm text-slate-300 hover:text-white hover:bg-white/5"
                    >
                      <Settings2 className="w-4 h-4" />
                      Beállítások
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** Lábléc / menü — süti panel újranyitása. */
export function CookieSettingsLink({ className = "" }) {
  const { openSettings } = useCookieConsent();
  return (
    <button
      type="button"
      data-testid="cookie-settings-link"
      onClick={openSettings}
      className={className || "hover:text-white transition-colors text-slate-400"}
    >
      Süti beállítások
    </button>
  );
}
